import React, { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { isCloudinaryConfigured } from '../../utils/cloudinaryUpload';
import { broadcastStockChange, subscribeToStockChanges } from '../../utils/stockSync';
import ProductForm from '../../components/admin/ProductForm';
import AdminProductCard from '../../components/admin/AdminProductCard';

export default function ProductsTab({ 
  products, 
  fetchData,
  metalCaratOptions,
  modelCategoryOptions,
  modelNameOptions = ['Mangalsutra', 'Cocktail', 'Box Chain', 'Rope Chain', 'Cable Chain', 'Figaro', 'Traditional', 'Modern', 'Solitaire', 'Eternity'],
  otherMaterialOptions = ['Steel', 'Rubber', 'Leather', 'Thread', 'Plastic']
}) {
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [cloudinaryEnabled, setCloudinaryEnabled] = useState(false);
  const [bulkDiscountModelName, setBulkDiscountModelName] = useState('');
  const [bulkDiscountPercent, setBulkDiscountPercent] = useState('');
  const [barcodeSearch, setBarcodeSearch] = useState('');

  useEffect(() => {
    const unsubscribe = subscribeToStockChanges(() => { fetchData(); });
    return () => unsubscribe();
  }, [fetchData]);

  useEffect(() => { isCloudinaryConfigured().then(setCloudinaryEnabled); }, []);

  const [productForm, setProductForm] = useState({
    name: '', category: 'gold', metal_type: 'Gold', metal_carat: '22k',
    model_category: 'Chain', model_name: '', weight: '', making_charges_percent: '',
    making_charges_type: 'percentage', discount_percent: '',
    stone_weight: '', stone_amount: '', diamond_cents: '', diamond_amount: '',
    diamond_quality: '', precious_stone_cents: '', precious_stone_amount: '',
    other_material_type: '', other_material_weight: '', other_material_amount: '',
    description: '', images: [''], image_focal_points: [{x:50,y:50}],
    is_featured: false, is_sold_out: false, hide_when_sold_out: false,
    length_enabled: false, length_options: [], color_enabled: false, color_options: [],
    stock_quantity: 0, is_by_order: false, gender: [], barcode: '',
    dimension_width_mm: '', dimension_height_mm: ''
  });

  const uniqueModelNames = [...new Set(products.filter(p => p.model_name).map(p => p.model_name))];

  const handleBulkDiscount = async () => {
    if (!bulkDiscountModelName || bulkDiscountPercent === '') {
      toast.error('Please select a model name and enter discount percentage');
      return;
    }
    try {
      await api.put(`/products/bulk-discount/model-name?model_name=${encodeURIComponent(bulkDiscountModelName)}&discount_percent=${bulkDiscountPercent}`);
      toast.success(`Discount applied to all "${bulkDiscountModelName}" products`);
      fetchData();
      setBulkDiscountModelName('');
      setBulkDiscountPercent('');
    } catch (error) {
      toast.error('Failed to apply bulk discount');
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const filteredImages = productForm.images.filter(img => img.trim() !== '');
      if (filteredImages.length === 0) { toast.error('Please add at least one image URL'); return; }

      const data = {
        ...productForm, images: filteredImages,
        image_focal_points: (productForm.image_focal_points || []).slice(0, filteredImages.length),
        weight: parseFloat(productForm.weight),
        making_charges_percent: parseFloat(productForm.making_charges_percent),
        discount_percent: productForm.discount_percent ? parseFloat(productForm.discount_percent) : 0,
        stone_weight: productForm.stone_weight ? parseFloat(productForm.stone_weight) : 0,
        stone_amount: productForm.stone_amount ? parseFloat(productForm.stone_amount) : 0,
        diamond_cents: productForm.diamond_cents ? parseFloat(productForm.diamond_cents) : 0,
        diamond_amount: productForm.diamond_amount ? parseFloat(productForm.diamond_amount) : 0,
        diamond_quality: productForm.diamond_quality || null,
        precious_stone_cents: productForm.precious_stone_cents ? parseFloat(productForm.precious_stone_cents) : 0,
        precious_stone_amount: productForm.precious_stone_amount ? parseFloat(productForm.precious_stone_amount) : 0,
        other_material_type: productForm.other_material_type || null,
        other_material_weight: productForm.other_material_weight ? parseFloat(productForm.other_material_weight) : 0,
        other_material_amount: productForm.other_material_amount ? parseFloat(productForm.other_material_amount) : 0,
        dimension_width_mm: productForm.dimension_width_mm ? parseFloat(productForm.dimension_width_mm) : null,
        dimension_height_mm: productForm.dimension_height_mm ? parseFloat(productForm.dimension_height_mm) : null,
      };

      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, data);
        toast.success('Product updated successfully');
      } else {
        await api.post('/products', data);
        toast.success('Product created successfully');
      }
      
      setShowProductForm(false);
      setEditingProduct(null);
      resetProductForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '', category: 'gold', metal_type: 'Gold', metal_carat: '22k',
      model_category: 'Chain', model_name: '', weight: '', making_charges_percent: '',
      making_charges_type: 'percentage', discount_percent: '',
      stone_weight: '', stone_amount: '', diamond_cents: '', diamond_amount: '',
      diamond_quality: '', precious_stone_cents: '', precious_stone_amount: '',
      other_material_type: '', other_material_weight: '', other_material_amount: '',
      description: '', images: [''], image_focal_points: [{x:50,y:50}],
      is_featured: false, is_sold_out: false, hide_when_sold_out: false,
      length_enabled: false, length_options: [], color_enabled: false, color_options: [],
      stock_quantity: 0, is_by_order: false, gender: [], barcode: '',
      dimension_width_mm: '', dimension_height_mm: ''
    });
  };

  const startEdit = (product) => {
    setEditingProduct(product);
    const images = (product.images || []).filter(img => img && img.trim() !== '');
    const actualImages = images.length > 0 ? images : [''];
    const focalPoints = product.image_focal_points || actualImages.map(() => ({x:50,y:50}));
    // Ensure focal points array matches images length
    const paddedFocals = actualImages.map((_, i) => focalPoints[i] || {x:50,y:50});
    setProductForm({
      name: product.name, category: product.category, metal_type: product.metal_type,
      metal_carat: product.metal_carat || '22k', model_category: product.model_category || 'Chain',
      model_name: product.model_name || '', weight: product.weight.toString(),
      making_charges_percent: product.making_charges_percent.toString(),
      making_charges_type: product.making_charges_type || 'percentage',
      discount_percent: product.discount_percent ? product.discount_percent.toString() : '',
      stone_weight: product.stone_weight ? product.stone_weight.toString() : '',
      stone_amount: product.stone_amount ? product.stone_amount.toString() : '',
      diamond_cents: product.diamond_cents ? product.diamond_cents.toString() : '',
      diamond_amount: product.diamond_amount ? product.diamond_amount.toString() : '',
      diamond_quality: product.diamond_quality || '',
      precious_stone_cents: product.precious_stone_cents ? product.precious_stone_cents.toString() : '',
      precious_stone_amount: product.precious_stone_amount ? product.precious_stone_amount.toString() : '',
      other_material_type: product.other_material_type || '',
      other_material_weight: product.other_material_weight ? product.other_material_weight.toString() : '',
      other_material_amount: product.other_material_amount ? product.other_material_amount.toString() : '',
      description: product.description, images: actualImages,
      image_focal_points: paddedFocals,
      is_featured: product.is_featured, is_sold_out: product.is_sold_out || false, hide_when_sold_out: product.hide_when_sold_out || false,
      length_enabled: product.length_enabled || false, length_options: product.length_options || [],
      color_enabled: product.color_enabled || false, color_options: product.color_options || [],
      stock_quantity: product.stock_quantity || 0, is_by_order: product.is_by_order || false,
      gender: product.gender || [], barcode: product.barcode || '',
      dimension_width_mm: product.dimension_width_mm ? product.dimension_width_mm.toString() : '',
      dimension_height_mm: product.dimension_height_mm ? product.dimension_height_mm.toString() : ''
    });
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const toggleSoldOut = async (productId, isCurrentlyUnavailable) => {
    try {
      if (isCurrentlyUnavailable) {
        await api.put(`/products/${productId}/mark-available`);
        toast.success('Product marked as available');
        broadcastStockChange(productId, { stock_quantity: 1, is_sold_out: false, action: 'mark-available' });
      } else {
        await api.put(`/products/${productId}/mark-sold`);
        toast.success('Product marked as sold out');
        broadcastStockChange(productId, { stock_quantity: 0, is_sold_out: true, action: 'mark-sold' });
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  return (
    <div data-testid="products-tab">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center flex-wrap gap-3">
        <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c]">Products</h2>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="text" value={barcodeSearch}
              onChange={(e) => setBarcodeSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              placeholder="Scan or type barcode..."
              className="pl-9 pr-7 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:border-[#1a3a5c] w-56"
              data-testid="barcode-search-input" autoComplete="off"
            />
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            {barcodeSearch && (
              <button onClick={() => setBarcodeSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
            )}
          </div>
          <button
            onClick={() => { setShowProductForm(true); setEditingProduct(null); resetProductForm(); }}
            className="bg-[#1a3a5c] text-white px-6 py-2 rounded-sm font-serif tracking-wide hover:bg-[#1f4268] transition-all duration-300 flex items-center space-x-2"
            data-testid="add-product-button"
          >
            <Plus className="h-5 w-5" />
            <span>Add Product</span>
          </button>
        </div>
      </div>

      {/* Bulk Discount */}
      {uniqueModelNames.length > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200 mb-6">
          <h3 className="font-serif text-lg font-semibold text-green-800 mb-3">Apply Bulk Discount by Model Name</h3>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">Model Name</label>
              <select value={bulkDiscountModelName} onChange={(e) => setBulkDiscountModelName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-green-600" data-testid="bulk-discount-model-select">
                <option value="">Select Model Name</option>
                {uniqueModelNames.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </div>
            <div className="w-32">
              <label className="block text-sm font-medium text-gray-700 mb-2">Discount %</label>
              <input type="number" min="0" max="100" step="0.01" value={bulkDiscountPercent} onChange={(e) => setBulkDiscountPercent(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-green-600" placeholder="e.g., 10" data-testid="bulk-discount-percent-input" />
            </div>
            <button type="button" onClick={handleBulkDiscount} className="bg-green-600 text-white px-6 py-2 rounded-sm hover:bg-green-700 transition-colors" data-testid="apply-bulk-discount-btn">Apply Discount</button>
          </div>
          <p className="text-xs text-gray-600 mt-2">This will apply the discount to all products with the selected model name</p>
        </div>
      )}

      {/* Product Form */}
      {showProductForm && (
        <ProductForm
          productForm={productForm}
          setProductForm={setProductForm}
          editingProduct={editingProduct}
          onSubmit={handleProductSubmit}
          onCancel={() => { setShowProductForm(false); setEditingProduct(null); }}
          metalCaratOptions={metalCaratOptions}
          modelCategoryOptions={modelCategoryOptions}
          modelNameOptions={modelNameOptions}
          otherMaterialOptions={otherMaterialOptions}
          cloudinaryEnabled={cloudinaryEnabled}
        />
      )}

      {/* Product Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products
          .filter(p => !barcodeSearch || (p.barcode && p.barcode.toLowerCase() === barcodeSearch.toLowerCase()))
          .map((product) => (
            <AdminProductCard
              key={product.id}
              product={product}
              onEdit={startEdit}
              onDelete={handleDeleteProduct}
              onToggleSoldOut={toggleSoldOut}
            />
          ))}
      </div>
    </div>
  );
}
